import React, { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Link, Typography, TypographyProps } from '@mui/material'

import languageService from '../../../../services/LanguageService'
import { useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'
import { buildBlueprintFamilyPath, buildEventCategoryPath, buildEventTypePath } from '../../../market-analytics/components/helpers/catalogueUtils'
import { useBlueprintFamilyForEvent } from '../../hooks/useBlueprintFamilyForEvent'
import { useLiveEventTagGroupWithEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { TrackedGame } from '../../types/TrackedGame'

export type LiveEventCompactTrailLevel = 'eventCategory' | 'eventType' | 'blueprintFamily'

const ALL_LEVELS: LiveEventCompactTrailLevel[] = ['eventCategory', 'eventType', 'blueprintFamily']

interface LiveEventCompactTrailProps {
  // Event type (tag) id of the event.
  eventTypeId?: string
  // Owning tracked game — carries the game-level blueprints and, on them, the
  // blueprint family each was categorized under.
  trackedGame?: TrackedGame
  // Game-level blueprint id of the event (`event.blueprintEvolutionData.blueprintId`).
  gameLevelBlueprintId?: string
  // Which levels to print, in hierarchy order. Surfaces that already name a
  // level elsewhere can narrow this to the ones they are missing.
  levels?: LiveEventCompactTrailLevel[]
  variant?: TypographyProps['variant']
  // Analytics links leave the current view in a new tab (feeds the user is
  // scrolling through).
  openInNewTab?: boolean
}

interface Crumb {
  key: LiveEventCompactTrailLevel
  label: string
  to?: string
}

/**
 * One-line `Event Category › Event Type › Blueprint Family` trail for the
 * surfaces where the event dialog's labelled hierarchy path is too heavy: the
 * news feed card sub header and the LET feed card. Same hooks, same
 * links, same role gating, so both agree with the event dialog on where an
 * event sits and where it leads.
 *
 * Silent levels: the family crumb only appears once its name is known (no
 * tracked game, no blueprint, uncategorized blueprint or no analytics
 * entitlement all leave it out, matching `useBlueprintFamilyForEvent`). The
 * category and type crumbs need only the event type id. Without
 * `RoleEnum.live_events_tracker` every crumb stays plain text.
 */
export const LiveEventCompactTrail: React.FC<LiveEventCompactTrailProps> = ({
  eventTypeId,
  trackedGame,
  gameLevelBlueprintId,
  levels = ALL_LEVELS,
  variant = 'caption',
  openInNewTab = false,
}) => {
  const { t } = useTranslation()
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const tagGroup = useLiveEventTagGroupWithEventTypeId(eventTypeId || '')
  const { familyId, familyName, canLink } = useBlueprintFamilyForEvent({ trackedGame, gameLevelBlueprintId })

  const crumbs = useMemo<Crumb[]>(() => {
    const result: Crumb[] = []
    const eventTypeName = eventTypeId ? languageService.getTranslation('tags', eventTypeId) : ''

    if (levels.includes('eventCategory') && tagGroup?.name) {
      result.push({
        key: 'eventCategory',
        label: tagGroup.name,
        to: hasLiveEventsTrackerAccess && tagGroup.id ? buildEventCategoryPath(tagGroup.id) : undefined,
      })
    }
    if (levels.includes('eventType') && eventTypeId && eventTypeName) {
      result.push({ key: 'eventType', label: eventTypeName, to: hasLiveEventsTrackerAccess ? buildEventTypePath(eventTypeId) : undefined })
    }
    if (levels.includes('blueprintFamily') && familyName) {
      result.push({ key: 'blueprintFamily', label: familyName, to: canLink && familyId ? buildBlueprintFamilyPath(familyId) : undefined })
    }
    return result
  }, [levels, tagGroup?.name, tagGroup?.id, eventTypeId, familyName, familyId, canLink, hasLiveEventsTrackerAccess])

  if (crumbs.length === 0) return null

  // A family crumb left on its own (no category or type name resolved) has no
  // trail to place it in, so it names its own level instead.
  const prefix = crumbs.length === 1 && crumbs[0].key === 'blueprintFamily' ? `${t('live-events:market_level_blueprint')}: ` : ''
  const newTabProps = openInNewTab ? { target: '_blank', rel: 'noopener' } : {}

  return (
    <Typography variant={variant} component="div" color="inherit" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      {prefix}
      {crumbs.map((crumb, index) => (
        <Fragment key={crumb.key}>
          {index > 0 ? <span aria-hidden="true"> › </span> : null}
          {crumb.to ? (
            <Link component={RouterLink} to={crumb.to} {...newTabProps}>
              {crumb.label}
            </Link>
          ) : (
            <span>{crumb.label}</span>
          )}
        </Fragment>
      ))}
    </Typography>
  )
}
